import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { _isNil, _noop, _notNil } from '@/littledash';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDetailType,
  SampleDefinitionDetailV1,
  SampleDefinitionFormData,
} from '@/model/SamplePlan.model';
import { MetadataGlossaryApiId, MetadataGlossaryV1 } from '@/model/Metadata.model';
import { unionSets } from '@/utils/browser.utils';
import { MapKeyType, MapValueType } from '@/model/Common.model';
import { TeamGlossary, TeamGlossaryApiId } from '@/model/Glosary.model';
import { DateUtils } from '@/utils/Date.utils';
import { AnimalApiId } from '@/model/Animal.model';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model';
import { isApiId } from '@/utils/ApiId.utils';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const no_assigned_group = 'unassigned';
export type SampleDefinitionRowData = Omit<SampleDefinitionFormData, 'details' | 'metadata' | 'timepoints'> & {
  sample_type_name: string;
  group_numbers: Array<number>;
  timepoints: string;
  metadata: Record<MetadataGlossaryApiId, string>;
  details: Partial<Record<SampleDetailType, SampleDefinitionDetailV1>>;
  collected_samples_count?: {
    planned?: number;
    collected?: number;
  };
};
type AnimalMap = Map<
  PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id'],
  ({ type: 'planned' } & PlannedAnimalTarget) | ({ type: 'registered' } & RegisteredAnimalTarget)
>;
export type GroupMap = Map<GroupTarget['api_id'], GroupTarget>;
export type UnassignedGroup = typeof no_assigned_group;
type GroupAnimalsMap = Map<
  GroupTarget['api_id'] | UnassignedGroup,
  Set<PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id']>
>;

export const enum TableOptions {
  none,
  display_registered_animal_column = 1 << 0,
  display_planned_animal_column = 1 << 1,
  display_selection_column = 1 << 2,
  collected_samples_count = 1 << 3,
  /**
   * Note: This flag uses inverted logic (hide vs show) to maintain backward compatibility.
   * When the feature flag is FALSE, this bit is SET to hide the column.
   * When the feature flag is TRUE, this bit is NOT SET, allowing the column to show.
   * TODO(m4rkmckenna) :: remove when sample execution completed
   */
  hide_calculate_collection_time_column = 1 << 4,
}

type OpenSection = {
  sample_definition?: SampleDefinitionFormData['api_id'];
  group?: GroupTarget['api_id'] | UnassignedGroup;
};
export type SectionType = keyof OpenSection;
type Counts = {
  planned: number;
  registered: number;
  capacity: number;
  samplesPlanned: number;
  samplesCollected: number;
};
type GroupCounts = Partial<Record<GroupTarget['api_id'] | UnassignedGroup, Counts>>;
type SampleDefinitionCounts = Counts & GroupCounts;
export type SamplePlanTableContext = {
  openSection: OpenSection;
  toggle: (type: SectionType, id: SampleDefinitionFormData['api_id'] | GroupTarget['api_id'] | UnassignedGroup) => void;
  updateRowSelection: (
    action:
      | { type: 'toggle'; value: SampleDefinitionRowData['api_id'] }
      | { type: 'select-all' }
      | { type: 'clear-all' }
  ) => void;
  animal_map: AnimalMap;
  group_map: GroupMap;
  group_animals: GroupAnimalsMap;
  sample_definition_rows: Map<SampleDefinitionFormData['api_id'], SampleDefinitionRowData>;
  sample_definition_groups: Map<SampleDefinitionFormData['api_id'], Set<MapKeyType<GroupAnimalsMap>>>;
  sample_definition_counts: Map<SampleDefinitionFormData['api_id'], SampleDefinitionCounts>;
  registered_animal_api_id_to_planned_animal_api_id: Map<AnimalApiId, AnimalDefinitionApiId>;
  sample_definition_animals: Map<
    SampleDefinitionFormData['api_id'],
    Set<RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id']>
  >;
  active_details: Set<SampleDetailType>;
  active_metadata: Set<MetadataGlossaryApiId>;
  metadata_glossary: Map<MetadataGlossaryApiId, MetadataGlossaryV1>;
  sample_glossary: Map<TeamGlossaryApiId, TeamGlossary>;
  table_options: TableOptions;
  selection: Set<SampleDefinitionRowData['api_id']>;
};
type SamplePlanTableProviderProps = {
  sample_definitions: Array<SampleDefinitionFormData>;
  planned_animals: Array<PlannedAnimalTarget>;
  registered_animals: Array<RegisteredAnimalTarget>;
  metadata_glossary: Array<MetadataGlossaryV1>;
  sample_glossary: Array<TeamGlossary>;
  groups: Array<GroupTarget>;
  selection?: SamplePlanTableContext['selection'];
  onSelectionChange?: (value: Exclude<SamplePlanTableProviderProps['selection'], undefined>) => void;
  displaySampleCounts?: boolean;
};

const context = createContext<SamplePlanTableContext>({
  openSection: {},
  toggle: _noop,
  updateRowSelection: _noop,
  group_animals: new Map(),
  sample_definition_rows: new Map(),
  group_map: new Map(),
  sample_definition_groups: new Map(),
  sample_definition_animals: new Map(),
  sample_definition_counts: new Map(),
  animal_map: new Map(),
  active_details: new Set(),
  active_metadata: new Set(),
  metadata_glossary: new Map(),
  sample_glossary: new Map(),
  registered_animal_api_id_to_planned_animal_api_id: new Map(),
  table_options: TableOptions.none,
  selection: new Set(),
});
export const optionSet = (flag: TableOptions, options: TableOptions): boolean => (options & flag) === flag;
export const useSamplePlanTableContext = () => useContext(context);
export const useOpenSection = (
  type: SectionType,
  id: SampleDefinitionFormData['api_id'] | GroupTarget['api_id'] | UnassignedGroup
): boolean => {
  const { openSection } = useSamplePlanTableContext();
  return useMemo(() => {
    return openSection?.[type] === id;
  }, [openSection, type, id]);
};

const toSampleDefinitionRow = (
  sample_definition: SampleDefinitionFormData,
  group_map: GroupMap,
  animal_map: AnimalMap,
  overrides: Partial<SampleDefinitionRowData> = {}
): SampleDefinitionRowData => {
  return {
    api_id: sample_definition.api_id,
    sample_type: sample_definition.sample_type,
    sample_type_name: '',
    name: sample_definition.name,
    calculate_collection_time: sample_definition.calculate_collection_time,
    timepoints: sample_definition.timepoints
      .map((timepoint) =>
        DateUtils.renderDuration(timepoint, {
          format: 'short',
          zeros: true,
          defaultResponse: '',
        })
      )
      .join(', '),
    target: sample_definition.target,
    metadata: sample_definition.metadata.reduce((acc, md) => ({ ...acc, [md.type]: md.value }), {}),
    details: sample_definition.details.reduce((acc, detail) => ({ ...acc, [detail.type]: detail }), {}),
    collected_samples_count: sample_definition.collected_samples_count,
    group_numbers: (() => {
      switch (sample_definition.target.type) {
        case 'groups':
          return sample_definition.target.targets.reduce<Array<number>>((acc, groupApiId) => {
            const group = group_map.get(groupApiId);
            return _notNil(group) ? [...acc, group.no] : acc;
          }, []);
        case 'planned_animals':
          return Array.from(
            sample_definition.target.targets.reduce((acc, apiId) => {
              const groupApiId = animal_map.get(apiId)?.study_group_api_id;
              if (_notNil(groupApiId)) {
                const groupNo = group_map.get(groupApiId)?.no;
                return _isNil(groupNo) ? acc : acc.add(groupNo);
              }
              return acc;
            }, new Set<number>())
          );
        case 'registered_animals':
          return Array.from(
            sample_definition.target.targets.reduce((acc, apiId) => {
              const groupApiId = animal_map.get(apiId)?.study_group_api_id;
              if (_notNil(groupApiId)) {
                const groupNo = group_map.get(groupApiId)?.no;
                return _isNil(groupNo) ? acc : acc.add(groupNo);
              }
              return acc;
            }, new Set<number>())
          );
        default:
          return [];
      }
    })().sort(),
    ...overrides,
  };
};
export const enableCalculateCollectionTimeSelector = createSelector(
  [featuresSelector],
  (features) => features?.sampling_sheets === true
);

export const SamplePlanTableProvider: FC<PropsWithChildren<SamplePlanTableProviderProps>> = ({
  children,
  sample_definitions,
  groups,
  registered_animals,
  planned_animals,
  sample_glossary,
  metadata_glossary,
  selection: incomingSelection,
  onSelectionChange,
  displaySampleCounts,
}) => {
  const [selection, updateSelection] = useState<SamplePlanTableContext['selection']>(new Set());
  const [openSection, setOpenSection] = useState<OpenSection>({});

  useEffect(() => updateSelection(new Set(incomingSelection)), [incomingSelection]);
  const enableCalculateCollectionTime = useSelector(enableCalculateCollectionTimeSelector);

  const toggle = useCallback<SamplePlanTableContext['toggle']>((type, id) => {
    setOpenSection((prev) => {
      const isCurrentlyOpen = prev[type] === id;

      if (isCurrentlyOpen) {
        const newPath = { ...prev };
        switch (type) {
          case 'sample_definition':
            delete newPath.sample_definition;
            delete newPath.group;
            break;
          case 'group':
            delete newPath.group;
            break;
        }
        return newPath;
      } else {
        const newPath = { ...prev, [type]: id };
        switch (type) {
          case 'sample_definition':
            delete newPath.group;
            break;
          case 'group':
            break;
        }
        return newPath;
      }
    });
  }, []);

  const metadataGlossaryMap = useMemo<Map<MetadataGlossaryApiId, MetadataGlossaryV1>>(() => {
    return new Map(metadata_glossary.map((mgl) => [mgl.api_id as MetadataGlossaryApiId, mgl]));
  }, [metadata_glossary]);
  const sampleGlossaryMap = useMemo<Map<TeamGlossaryApiId, TeamGlossary>>(() => {
    return new Map(sample_glossary.map((sample) => [sample.api_id, sample]));
  }, [sample_glossary]);

  const tableRenderData = useMemo<
    Pick<
      SamplePlanTableContext,
      | 'sample_definition_rows'
      | 'sample_definition_groups'
      | 'group_animals'
      | 'group_map'
      | 'animal_map'
      | 'sample_definition_animals'
      | 'active_details'
      | 'active_metadata'
      | 'sample_definition_counts'
      | 'table_options'
      | 'registered_animal_api_id_to_planned_animal_api_id'
    >
  >(() => {
    const group_map: GroupMap = new Map(groups.map((group) => [group.api_id, group]));
    const animal_map: AnimalMap = new Map();
    const registered_animal_api_id_to_planned_animal_api_id = new Map<AnimalApiId, AnimalDefinitionApiId>();
    const sample_definition_animals: SamplePlanTableContext['sample_definition_animals'] = new Map();
    const sample_definition_groups: SamplePlanTableContext['sample_definition_groups'] = new Map();
    const active_details: SamplePlanTableContext['active_details'] = new Set();
    const active_metadata: SamplePlanTableContext['active_metadata'] = new Set();
    const sample_definition_rows: SamplePlanTableContext['sample_definition_rows'] = new Map();
    const sample_definition_counts: SamplePlanTableContext['sample_definition_counts'] = new Map();
    let table_options = enableCalculateCollectionTime
      ? TableOptions.none
      : TableOptions.hide_calculate_collection_time_column;
    if (displaySampleCounts === true) {
      table_options = table_options | TableOptions.collected_samples_count;
    }

    const group_animals: SamplePlanTableContext['group_animals'] = new Map();
    planned_animals.forEach((animal) => {
      const groupApiId = animal.study_group_api_id ?? no_assigned_group;
      const animals = group_animals.get(groupApiId);
      group_animals.set(
        groupApiId,
        _isNil(animals)
          ? new Set<PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id']>([animal.api_id])
          : animals.add(animal.api_id)
      );
      animal_map.set(animal.api_id, { type: 'planned', ...animal });
      if (isApiId('adf', animal.api_id, false) && isApiId('aml', animal.animal_api_id, false)) {
        registered_animal_api_id_to_planned_animal_api_id.set(animal.animal_api_id, animal.api_id);
      }
    });
    registered_animals.forEach((animal) => {
      const groupApiId = animal.study_group_api_id ?? no_assigned_group;
      const animals = group_animals.get(groupApiId);
      group_animals.set(
        groupApiId,
        _isNil(animals)
          ? new Set<PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id']>([animal.api_id])
          : animals.add(animal.api_id)
      );
      animal_map.set(animal.api_id, { type: 'registered', ...animal });
    });

    sample_definitions.forEach((sample_definition) => {
      sample_definition.details.forEach((detail) => active_details.add(detail.type));
      sample_definition.metadata.forEach((md) => {
        if (metadataGlossaryMap.has(md.type)) {
          active_metadata.add(md.type);
        }
      });
      sample_definition_rows.set(
        sample_definition.api_id,
        toSampleDefinitionRow(sample_definition, group_map, animal_map, {
          sample_type_name: sampleGlossaryMap.get(sample_definition.sample_type)?.title ?? '',
        })
      );
      switch (sample_definition.target.type) {
        case 'groups': {
          const currentGroups = sample_definition_groups.get(sample_definition.api_id);
          sample_definition_groups.set(
            sample_definition.api_id,
            _isNil(currentGroups)
              ? new Set(sample_definition.target.targets)
              : unionSets(currentGroups, new Set(sample_definition.target.targets))
          );
          const animals = sample_definition.target.targets.reduce<
            MapValueType<SamplePlanTableContext['sample_definition_animals']>
          >((acc, target) => {
            const animals = group_animals.get(target);
            return _notNil(animals) ? unionSets(acc, animals) : acc;
          }, new Set());
          sample_definition_animals.set(sample_definition.api_id, animals);
          break;
        }
        case 'registered_animals': {
          const registeredAnimalGroups = sample_definition.target.targets.reduce(
            (acc, animalApiId) => acc.add(animal_map.get(animalApiId)?.study_group_api_id ?? no_assigned_group),
            new Set<GroupTarget['api_id'] | UnassignedGroup>()
          );
          if (registeredAnimalGroups.size > 0) {
            const currentGroups = sample_definition_groups.get(sample_definition.api_id);
            sample_definition_groups.set(
              sample_definition.api_id,
              _isNil(currentGroups) ? registeredAnimalGroups : unionSets(currentGroups, registeredAnimalGroups)
            );
          }
          sample_definition_animals.set(sample_definition.api_id, new Set(sample_definition.target.targets));
          break;
        }
        case 'planned_animals': {
          const { targets } = sample_definition.target;
          const allAnimalsForSample = new Set<RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id']>(
            targets
          );
          registered_animal_api_id_to_planned_animal_api_id.forEach((plannedAnimalId, registeredAnimalId) => {
            if (targets.includes(plannedAnimalId)) {
              allAnimalsForSample.add(registeredAnimalId);
            }
          });

          const plannedAnimalGroups = Array.from(allAnimalsForSample).reduce(
            (acc, animalApiId) => acc.add(animal_map.get(animalApiId)?.study_group_api_id ?? no_assigned_group),
            new Set<GroupTarget['api_id'] | UnassignedGroup>()
          );
          if (plannedAnimalGroups.size > 0) {
            const currentGroups = sample_definition_groups.get(sample_definition.api_id);
            sample_definition_groups.set(
              sample_definition.api_id,
              _isNil(currentGroups) ? plannedAnimalGroups : unionSets(currentGroups, plannedAnimalGroups)
            );
          }
          sample_definition_animals.set(sample_definition.api_id, allAnimalsForSample);
          break;
        }
      }
    });

    sample_definitions.forEach((sample_definition) => {
      const globalCounts: Counts = { planned: 0, capacity: 0, registered: 0, samplesPlanned: 0, samplesCollected: 0 };
      const groupCounts: GroupCounts = {};
      sample_definition_animals.get(sample_definition.api_id)?.forEach((animalId) => {
        const animal = animal_map.get(animalId);
        if (_notNil(animal)) {
          const groupId = animal?.study_group_api_id ?? no_assigned_group;
          const groupCount = groupCounts?.[groupId] ?? {
            planned: 0,
            registered: 0,
            capacity: 0,
            samplesPlanned: 0,
            samplesCollected: 0,
          };
          switch (animal.type) {
            case 'registered':
              table_options = table_options | TableOptions.display_registered_animal_column;
              globalCounts.registered++;
              groupCount.registered++;
              if (sample_definition.target.type === 'registered_animals') {
                globalCounts.capacity++;
                groupCount.capacity++;
              }
              break;
            case 'planned':
              table_options = table_options | TableOptions.display_planned_animal_column;
              globalCounts.planned++;
              groupCount.planned++;
              if (sample_definition.target.type === 'planned_animals') {
                globalCounts.capacity++;
                groupCount.capacity++;
              }
              break;
          }
          groupCounts[groupId] = groupCount;
        }
      });
      if (sample_definition.target.type === 'groups') {
        const collectedSampleCountsForGroups = sample_definition.collected_samples_count?.groups;

        sample_definition.target.targets.forEach((groupApiId) => {
          const group = group_map.get(groupApiId);
          if (_notNil(group)) {
            const groupCount = groupCounts?.[groupApiId] ?? {
              planned: 0,
              registered: 0,
              capacity: 0,
              samplesPlanned: 0,
              samplesCollected: 0,
            };
            globalCounts.capacity += group.max_subjects;
            groupCount.capacity = group.max_subjects;
            if (_notNil(collectedSampleCountsForGroups) && _notNil(collectedSampleCountsForGroups[groupApiId])) {
              groupCount.samplesPlanned = collectedSampleCountsForGroups[groupApiId].planned;
              groupCount.samplesCollected = collectedSampleCountsForGroups[groupApiId].collected;
            }
            groupCounts[groupApiId] = groupCount;
          }
        });
      }
      sample_definition_counts.set(sample_definition.api_id, {
        ...globalCounts,
        ...groupCounts,
      } as SampleDefinitionCounts);
    });
    return {
      animal_map,
      group_map,
      sample_definition_groups,
      sample_definition_animals,
      sample_definition_rows,
      sample_definition_counts,
      group_animals,
      active_details,
      active_metadata,
      table_options,
      registered_animal_api_id_to_planned_animal_api_id,
    };
  }, [
    sample_definitions,
    groups,
    registered_animals,
    planned_animals,
    sampleGlossaryMap,
    displaySampleCounts,
    enableCalculateCollectionTime,
  ]);
  const updateRowSelection = useCallback<SamplePlanTableContext['updateRowSelection']>(
    (action) => {
      switch (action.type) {
        case 'toggle':
          updateSelection((prevState) => {
            if (prevState.has(action.value)) {
              prevState.delete(action.value);
            } else {
              prevState.add(action.value);
            }
            return new Set(prevState);
          });
          break;
        case 'select-all':
          updateSelection(new Set(tableRenderData.sample_definition_rows.keys()));
          break;
        case 'clear-all':
          updateSelection(new Set());
          break;
      }
    },
    [tableRenderData.sample_definition_rows]
  );

  const selectionEnabled = _notNil(incomingSelection) && _notNil(onSelectionChange);
  const value = useMemo<SamplePlanTableContext>(
    () => ({
      openSection,
      toggle,
      updateRowSelection,
      sample_glossary: sampleGlossaryMap,
      metadata_glossary: metadataGlossaryMap,
      selection,
      ...tableRenderData,
      table_options: selectionEnabled
        ? tableRenderData.table_options | TableOptions.display_selection_column
        : tableRenderData.table_options,
    }),
    [
      openSection,
      toggle,
      updateRowSelection,
      tableRenderData,
      sampleGlossaryMap,
      metadataGlossaryMap,
      selection,
      selectionEnabled,
    ]
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};
